import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  getBatteryOptimizationStatus,
  requestBatteryOptimizationExemption,
} from '../utils/backgroundLocation';

const DISMISS_KEY = 'mt_batt_notice_dismissed';

/**
 * Shown after a driver is approved, while Android battery optimization is still
 * limiting the app. First surfacing is a full explainer card; once dismissed it
 * drops to a passive one-line banner. Never fires the system dialog on its own —
 * only an explicit button press does. Hides itself for good once the exemption
 * is granted (re-checked whenever the app regains focus).
 */
export default function BatteryOptimizationNotice({ t }) {
  const [status, setStatus] = useState(null); // { supported, ignoring }
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === '1',
  );
  const [showOem, setShowOem] = useState(false);
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    const next = await getBatteryOptimizationStatus();
    if (mounted.current) setStatus(next);
  }, []);

  useEffect(() => {
    mounted.current = true;
    refresh();
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', refresh);
    return () => {
      mounted.current = false;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', refresh);
    };
  }, [refresh]);

  // Nothing to do: still loading, not an Android device, or already exempt.
  if (!status || !status.supported || status.ignoring) return null;

  const allow = async () => {
    setBusy(true);
    try {
      const opened = await requestBatteryOptimizationExemption();
      if (!opened) toast.error(t('battery.openFailed'));
      // The driver leaves to the system screen; refresh() on return updates state.
    } finally {
      if (mounted.current) setBusy(false);
    }
  };

  const later = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  if (dismissed) {
    return (
      <button type="button" className="batt-banner" onClick={allow} disabled={busy}>
        <span>{t('battery.bannerText')}</span>
        <span className="batt-banner-cta">{t('battery.bannerFix')}</span>
      </button>
    );
  }

  return (
    <div className="notif-prompt batt-notice">
      <div className="notif-prompt-copy">
        <strong>{t('battery.title')}</strong>
        <p className="text-muted">{t('battery.body')}</p>
        <button
          type="button"
          className="batt-oem-toggle"
          onClick={() => setShowOem((v) => !v)}
        >
          {t('battery.oemToggle')}
        </button>
        {showOem && <p className="text-muted batt-oem-help">{t('battery.oemHelp')}</p>}
      </div>
      <div className="notif-prompt-actions">
        <button type="button" className="btn btn-ghost btn-sm" onClick={later} disabled={busy}>
          {t('battery.later')}
        </button>
        <button type="button" className="btn btn-green btn-sm" onClick={allow} disabled={busy}>
          {t('battery.allow')}
        </button>
      </div>
    </div>
  );
}
